import React from 'react';
import Pagination from '../../Pagination';
import StepFooter from './StepFooter';
import { useMoneyFormat, useDateFormat } from '../../../context/LocaleContext';
import { EXPENSE_PAYEE_MAX, EXPENSE_DESCRIPTION_MAX } from '../../../domain/expenses';
import { MAX_MONEY_MINOR } from '../../../domain/money';
import {
  importCategoryProblemText, importTooLongText, importTooLargeText, importSkippedNotice, importButtonLabel,
} from '../../../domain/importDelimited';
import s from '../import.module.css';

// A row that cannot be imported is shown, tinted and with its bad cell named,
// rather than dropped: an import that lands 47 of 50 rows without saying so is
// worse than one that names the three.
// Why a category was refused, and a cell too long to store, are said by
// `importCategoryProblemText` and `importTooLongText` in
// src/domain/importDelimited.js.
const categoryProblemText = importCategoryProblemText;
const tooLong = importTooLongText;

function PreviewRow({ row }) {
  // The studio's date pattern and currency, both from the one Region setting —
  // see src/domain/locale.js. formatDateOnly substitutes into the ISO string
  // rather than parsing it, so a preview row still cannot slip a day.
  const formatMoney = useMoneyFormat();
  const shortDate = useDateFormat();
  const bad = row.problems.length > 0;
  const badDate = row.problems.includes('date');
  const badAmount = row.problems.includes('amount');
  const badCategory = row.problems.includes('category');
  const badPayee = row.problems.includes('payee');
  const badDescription = row.problems.includes('description');
  // Read rather than unreadable, and absurd (#155). "no amount" would be the
  // wrong sentence — the cell parsed perfectly well, it is a mis-mapped column,
  // and the ceiling is what says so. In the studio's own currency, because a
  // count of minor units is not a thing anybody recognises.
  const hugeAmount = row.amountVerdict === 'too_large';
  const tooLarge = importTooLargeText(formatMoney(MAX_MONEY_MINOR));

  return (
    <tr className={bad ? s.rowProblem : undefined}>
      <td className={badDate ? s.cellProblem : undefined}>
        {row.occurredOn ? shortDate(row.occurredOn) : `Row ${row.line}: no date`}
      </td>
      <td className={badAmount ? s.cellProblem : undefined}>
        {hugeAmount && row.type !== 'revenue' ? tooLarge
          : row.type === 'expense'
            ? formatMoney(row.amountMinor)
            : (badAmount ? 'no amount' : '–')}
      </td>
      <td className={hugeAmount && row.type === 'revenue' ? s.cellProblem : undefined}>
        {hugeAmount && row.type === 'revenue' ? tooLarge
          : row.type === 'revenue' ? formatMoney(row.amountMinor) : '–'}
      </td>
      <td className={badPayee ? s.cellProblem : undefined}>
        {badPayee ? tooLong(row.payee, EXPENSE_PAYEE_MAX) : (row.payee || '–')}
      </td>
      {/* An importable row always names one of the studio's own categories, so
          the only other thing this cell can hold is the dash a row refused on
          its date or its amount gets, whose category was not judged
          (`buildImportRows`). */}
      <td className={badCategory ? s.cellProblem : undefined}>
        {badCategory ? categoryProblemText(row) : (row.category || '–')}
      </td>
      <td className={badDescription ? s.cellProblem : undefined}>
        {badDescription
          ? tooLong(row.description, EXPENSE_DESCRIPTION_MAX)
          : (row.description || '–')}
      </td>
    </tr>
  );
}

export default function PreviewStep({
  rows, summary, page, pageCount, perPage, total,
  onPageChange, onPerPageChange, onBack, onCancel, onImport, busy,
}) {
  return (
    <>
      <div className={`${s.imCard} ${s.imWide}`}>
        <div className={s.imStep}>Step 4/4</div>
        <div className={s.imQ}>Import Preview</div>
        <div className={s.imHint}>
          Check that your data is mapped to the correct fields. If something is not right, go back
          and match the columns differently. When you are ready, click Import.
        </div>
        {summary.skipped ? (
          <div className={s.imWarn}>
            {importSkippedNotice(summary)}
          </div>
        ) : null}
      </div>

      <div className={s.imWrap}>
        <table className={s.imT}>
          <thead>
            <tr>
              <th>Transaction Date</th><th>Expense</th><th>Revenue</th>
              <th>Payee</th><th>Category</th><th>Description</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => <PreviewRow key={r.line} row={r} />)}
          </tbody>
        </table>
        <Pagination
          page={page}
          pageCount={pageCount}
          perPage={perPage}
          total={total}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
        />
      </div>

      <div className={`${s.imCard} ${s.imWide}`}>
        <StepFooter
          bare
          onBack={onBack}
          onCancel={onCancel}
          onNext={onImport}
          busy={busy}
          nextDisabled={!summary.importable}
          nextLabel={importButtonLabel(summary)}
        />
      </div>
    </>
  );
}
